import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from '@phosphor-icons/react';
import { cn } from '@/lib/utils';

/** Painéis modais pensados para o polegar: sobem por baixo (`bottom`) ou tomam
 *  a tela inteira (`full`). Diferente de `drawer.tsx` (desliza da direita e
 *  serve o catálogo), aqui a altura é sempre `dvh` e o rodapé respeita a
 *  safe-area — sem isso o botão de ação some atrás da barra do navegador ou da
 *  gesture bar no iOS/Android. */

const Sheet = DialogPrimitive.Root;
const SheetTrigger = DialogPrimitive.Trigger;
const SheetClose = DialogPrimitive.Close;
const SheetPortal = DialogPrimitive.Portal;

const SheetOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      'fixed inset-0 z-50 bg-black/50 backdrop-blur-sm',
      'data-[state=open]:animate-in data-[state=closed]:animate-out',
      'data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0',
      className
    )}
    {...props}
  />
));
SheetOverlay.displayName = DialogPrimitive.Overlay.displayName;

interface SheetContentProps
  extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> {
  side?: 'bottom' | 'full';
  /**
   * Classe extra para o escurecimento. Existe porque `Drawer` e `Sheet` são
   * compartilhados com o self-service e com o catálogo de kits: mudar o
   * `bg-black/50` padrão escureceria todos os painéis do app para atender a um.
   */
  overlayClassName?: string;
  /** Oculta o "X" — use quando o cabeçalho já traz um botão de voltar. */
  hideClose?: boolean;
}

const SheetContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  SheetContentProps
>(
  (
    {
      className,
      children,
      side = 'bottom',
      hideClose = false,
      overlayClassName,
      ...props
    },
    ref
  ) => (
    <SheetPortal>
      <SheetOverlay className={overlayClassName} />
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          'fixed z-50 flex flex-col bg-card text-card-foreground shadow-xl',
          'data-[state=open]:animate-in data-[state=closed]:animate-out',
          'data-[state=open]:duration-300 data-[state=closed]:duration-200',
          // Celular: painel colado na base / tela cheia.
          // Desktop: diálogo centrado — um sheet de 1920px encostado no rodapé
          // não é o padrão de nenhum sistema de janelas.
          //
          // A centralização usa `inset-0 + m-auto` em vez de
          // `left-1/2 -translate-x-1/2`: as animações de slide escrevem
          // `transform`, e sobrescreveriam o translate que centraliza.
          side === 'bottom' && [
            'inset-x-0 bottom-0 max-h-[85dvh] rounded-t-2xl border-t border-border',
            'data-[state=open]:slide-in-from-bottom data-[state=closed]:slide-out-to-bottom',
            'sm:inset-0 sm:m-auto sm:h-fit sm:w-[calc(100%-3rem)] sm:max-w-lg',
            'sm:rounded-2xl sm:border sm:border-border',
            'sm:data-[state=open]:slide-in-from-bottom-4 sm:data-[state=closed]:slide-out-to-bottom-4',
            'sm:data-[state=open]:zoom-in-95 sm:data-[state=closed]:zoom-out-95',
          ],
          side === 'full' && [
            'inset-0 h-[100dvh] w-full',
            'data-[state=open]:slide-in-from-right data-[state=closed]:slide-out-to-right',
            'sm:m-auto sm:h-[85dvh] sm:w-[calc(100%-4rem)] sm:max-w-3xl',
            'sm:rounded-2xl sm:border sm:border-border',
            // Zera o deslocamento horizontal do slide do celular — sem isto o
            // diálogo centrado entraria voando da direita.
            'sm:data-[state=open]:slide-in-from-right-0 sm:data-[state=closed]:slide-out-to-right-0',
            'sm:data-[state=open]:slide-in-from-bottom-4 sm:data-[state=closed]:slide-out-to-bottom-4',
            'sm:data-[state=open]:zoom-in-95 sm:data-[state=closed]:zoom-out-95',
          ],
          className
        )}
        {...props}
      >
        {/* A "alça" só faz sentido onde o painel sobe da base — no diálogo
            centrado do desktop ela vira enfeite sem função. */}
        {side === 'bottom' && (
          <span
            aria-hidden
            className="mx-auto mt-2.5 h-1.5 w-11 shrink-0 rounded-full bg-border sm:hidden"
          />
        )}
        {children}
        {!hideClose && (
          <DialogPrimitive.Close
            className={cn(
              'absolute right-3 top-3 flex h-11 w-11 items-center justify-center',
              'rounded-md text-muted-foreground transition-colors',
              'hover:bg-accent hover:text-accent-foreground'
            )}
            aria-label="Fechar"
          >
            <X size={22} />
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </SheetPortal>
  )
);
SheetContent.displayName = DialogPrimitive.Content.displayName;

function SheetHeader({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        'flex shrink-0 flex-col gap-1 border-b border-border px-4 py-4 pr-16 sm:px-6',
        className
      )}
      {...props}
    />
  );
}

/** Corpo rolável. O `overscroll-contain` impede que o scroll "vaze" para a
 *  página atrás quando o painel chega ao fim — no mobile isso fecha o teclado
 *  e desloca o conteúdo sem que o cliente tenha pedido.
 *
 *  `min-h-0` é obrigatório, não cosmético: o padrão de um item de flex é
 *  `min-height: auto`, que o impede de encolher abaixo da altura do próprio
 *  conteúdo. Sem ele o `flex-1 overflow-y-auto` não vira caixa de scroll — o
 *  corpo cresce até o tamanho do conteúdo, transborda o `max-h-[85dvh]` do
 *  painel e não sobra nada para a roda do mouse rolar. O `overscroll-contain`
 *  logo acima piorava a sensação: numa caixa que não rola ele ainda assim
 *  segura o evento, então a roda não faz nada em vez de rolar a página atrás.
 *
 *  Aceita `ref` (prop normal no React 19): este é o container de scroll do
 *  painel, e quem troca o conteúdo por baixo precisa poder voltar ao topo. */
function SheetBody({
  className,
  ...props
}: React.ComponentPropsWithRef<'div'>) {
  return (
    <div
      className={cn(
        'min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-4 sm:px-6',
        className
      )}
      {...props}
    />
  );
}

/** `sm:rounded-b-2xl` acompanha o `sm:rounded-2xl` do painel: o rodapé tem
 *  `bg-card` próprio e, com cantos retos, pintava por cima do arredondamento do
 *  container no diálogo centrado do desktop. No celular o painel encosta na
 *  base da tela e a barra reta é o correto. Arredondar o filho em vez de pôr
 *  `overflow-hidden` no pai preserva o focus ring de 4px, que é desenhado para
 *  fora do elemento e seria cortado. */
function SheetFooter({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        'flex shrink-0 flex-col gap-3 border-t border-border bg-card px-4 pt-4 sm:rounded-b-2xl sm:px-6',
        className
      )}
      style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}
      {...props}
    />
  );
}

const SheetTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      'text-lg font-semibold leading-tight tracking-tight text-foreground',
      className
    )}
    {...props}
  />
));
SheetTitle.displayName = DialogPrimitive.Title.displayName;

const SheetDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('text-sm leading-relaxed text-muted-foreground', className)}
    {...props}
  />
));
SheetDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Sheet,
  SheetTrigger,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetBody,
  SheetFooter,
  SheetTitle,
  SheetDescription,
};
